import React, { useState, useEffect } from "react";
import VesselHeader from "../../components/Vessel/VesselHeader";
import EmissionCard from "../../components/Vessel/VesselEmission/EmissionCard";
import FuelParameterChart from "../../components/Vessel/VesselEmission/FuelParameterChart";
import ShipLoadingState from "../../components/Vessel/VesselEmission/ShipLoadingState";
import EmissionOverviewChart from "../../components/Vessel/VesselEmission/EmissionOverviewChart";
import VesselStateTransition from "../../components/Vessel/VesselEmission/VesselStateTransition";
import FuelConsChart from "../../components/Vessel/VesselEmission/FuelConsChart";
import { Cloud, Zap, Flame, Wind, Factory, Atom, Fuel, Rocket } from "lucide-react";
import DateRangePicker from "../../components/Common/DateRangePicker";
import { useSelector, useDispatch } from "react-redux";
import { getVesselEmissionSummary } from "../../services/vesselEmissionService";
import VoyageFilter from "../../components/Common/VoyageFilter";
import { clearSelectedVoyage } from "../../store/voyageFilterSlice";

const VesselEmission = () => {
  const dispatch = useDispatch();
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [vesselSummary, setVesselSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);
  const [ dateRangeDisabled, setDateDisabled ] = useState(false); 

  useEffect(() => {
    dispatch(clearSelectedVoyage());
  }, [currentVessel?._id, dispatch]);

    useEffect(() => {
    setDateDisabled(!!selectedVoyage);
  }, [selectedVoyage]);

  useEffect(() => {
    const fetchSummary = async () => {
    if (!currentVessel?._id) return;
    
    if (!selectedVoyage && (!startDate || !endDate)) return;

      setLoading(true);
      try {
        const response = await getVesselEmissionSummary(
          currentVessel._id,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        );

        console.log("API Response:", response);

        if (response?.status === "error") {
          setError("Failed to fetch emission data");
          setVesselSummary(null);
        } else {
          setVesselSummary(response);
          setError(null);
        }
      } catch (err) {
        setError(err.message);
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [currentVessel, startDate, endDate, selectedVoyage]);

  const emissionTypes = {
    co2: {
      label: "Carbon Dioxide",
      symbol: "CO₂",
      icon: () => <Cloud className="w-5 h-5" />,
    },
    ch4: {
      label: "Methane",
      symbol: "CH₄",
      icon: () => <Fuel className="w-5 h-5" />,
    },
    n20: {
      label: "Nitrous oxide",
      symbol: "N₂O",
      icon: () => <Rocket className="w-5 h-5" />,
    },
    co2equivalent: {
      label: "Carbon Dioxide Equivalent",
      symbol: "CO₂ Equivalent",
      icon: () => <Cloud className="w-5 h-5" />,
    },
    ghg: {
      label: "Greenhouse Gas Emission",
      symbol: "GHG Intensity",
      icon: () => <Atom className="w-5 h-5" />,
    },
    sox: {
      label: "Sulfur Oxides",
      symbol: "SOₓ",
      icon: () => <Flame className="w-5 h-5" />,
    },
    nox: {
      label: "Nitrogen Oxides",
      symbol: "NOₓ",
      icon: () => <Zap className="w-5 h-5" />,
    },
    pm: {
      label: "Particulate Matter",
      symbol: "PM",
      icon: () => <Wind className="w-5 h-5" />,
    },
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="w-full mx-auto">
        <div className="flex justify-between">
          <VesselHeader pageTitle="Emission Analytics" />

          <div className="flex">
            <VoyageFilter />
            <DateRangePicker disabled={dateRangeDisabled} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6 mb-10">
          {Object.entries(emissionTypes).map(([key, meta]) => (
            <EmissionCard
              type={key}
              data={vesselSummary}
              emissionTypes={emissionTypes}
            />
          ))}
        </div>

        {/* Main Chart Section */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm mb-6">
          <EmissionOverviewChart voyageId={selectedVoyage?._id || null} />
        </div>

        {/* Two-Column Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
            <FuelParameterChart voyageId={selectedVoyage?._id || null} />
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
            <ShipLoadingState voyageId={selectedVoyage?._id || null} />
          </div>
        </div>

        {/* Two-Column Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
            <VesselStateTransition voyageId={selectedVoyage?._id || null} />
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
            <FuelConsChart voyageId={selectedVoyage?._id || null} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VesselEmission;